import Vue from 'vue'
import type { RouteConfig } from 'vue-router'
import VueRouter from 'vue-router'
import { Store } from 'vuex'
import { MAINTENANCE_MODE } from './config'
import { isPlayground } from '@/environment'
import { isTest } from './environment'
import { AppState } from './vuex'

if (typeof window !== 'undefined' && !isTest) {
  // This check is here for unit testing
  // See https://stackoverflow.com/questions/55523639/vue-test-utils-could-not-overwrite-property-route-this-is-usually-caused-by-a/55544303#55544303
  Vue.use(VueRouter)
}

export const paths = {
  home: '/',
  ruleBuilder: '/rules/:rule_id/:branch?',
  rulesPage: '/rules',
  policyBuilder: '/policies/:policy_id/:branch?',
  policyAnalysis: '/policies/:policy_id/:branch/analysis',
  policiesPage: '/policies',
  userFunctionBuilder: '/user_functions/:user_function_id/:branch?',
  userFunctionsPage: '/user_functions',
  auth: '/auth',
  logout: '/logout',
  login: '/login',
  search: '/search',
  notFound: '/*',
}

type PathsType = typeof paths
type ViewName = keyof PathsType

const viewNameToTitle: { [key in ViewName]: string } = {
  home: 'Home',
  ruleBuilder: 'Rule Builder',
  policyBuilder: 'Policy Builder',
  policyAnalysis: 'Policy Analysis',
  userFunctionBuilder: 'Function Builder',
  auth: 'Logging in',
  logout: 'Logout',
  login: 'Login',
  rulesPage: 'Rules',
  userFunctionsPage: 'User Functions',
  policiesPage: 'Policies',
  notFound: 'Page Not Found',
  search: 'Search',
}

export const getViewTitle = (viewName: string) => {
  let title = viewNameToTitle[viewName as ViewName] || ''

  if (isPlayground) {
    title += ` -- PLAYGROUND`
  }

  return title
}

type NextFunc = (arg?: any) => any

export const createIsValidAuth = (store: Store<AppState>) => (to: any, from: any, next: NextFunc) => {
  const token = localStorage.getItem('token')

  if (!token) {
    next({ name: 'logout' })
    return
  }

  const promises: Promise<any>[] = []

  if (!store.direct.state.auth.user) {
    promises.push(store.direct.dispatch.auth.UPDATE_AUTH_TOKEN(token))
  }

  const routesNeedKeysMeta = ['/', '/rules', '/policies', '/user_functions']
  if (routesNeedKeysMeta.some(v => window.location.pathname.includes(v)) || Object.hasOwn(window, 'Cypress')) {
    // Make sure we've loaded required data before going to any view requiring auth
    if (!store.state.scriptKey.inputs) {
      promises.push(store.direct.dispatch.scriptKey.GET_INPUTS_METADATA())
    }

    if (!store.state.scriptKey.helpers) {
      promises.push(store.direct.dispatch.scriptKey.GET_HELPERS_METADATA())
    }
  }

  Promise.all(promises).then(next)
}

// Need to store a reference
let router: VueRouter | undefined

export const getRouter = () => router

type IrisRouteConfig = RouteConfig & { name: ViewName }

export const createRouter = (store: Store<AppState>) => {
  const isValidAuth = createIsValidAuth(store)

  const irisRoutes: IrisRouteConfig[] = [
    {
      path: paths.logout,
      name: 'logout',
      component: () => import('@/components/LogoutPage/Logout.vue'),
    },
    {
      path: paths.login,
      name: 'login',
      component: () => import('@/components/LoginPage/Login.vue'),
      meta: { title: 'Login - Iris' },
    },
    {
      path: paths.auth,
      name: 'auth',
      component: () => import('@/components/AuthPage/Auth.vue'),
    },
    {
      path: paths.home,
      name: 'home',
      component: () => import('@/components/HomePage/Home.vue'),
      beforeEnter: isValidAuth,
      meta: { title: 'Home - Iris' },
    },
    {
      path: paths.ruleBuilder,
      name: 'ruleBuilder',
      component: () => import('@/components/BuildersPages/RuleBuilderPage/RuleBuilder.vue'),
      beforeEnter: isValidAuth,
    },
    {
      path: paths.policyBuilder,
      name: 'policyBuilder',
      component: () => import('@/components/BuildersPages/PolicyBuilderPage/PolicyBuilder.vue'),
      beforeEnter: isValidAuth,
      children: [
        {
          path: paths.policyAnalysis,
          component: () => import('@/components/common/PolicyAnalysis.vue'),
        },
      ],
    },
    {
      path: paths.userFunctionBuilder,
      name: 'userFunctionBuilder',
      component: () => import('@/components/BuildersPages/UserFunctionPage/UserFunctionBuilder.vue'),
      beforeEnter: isValidAuth,
    },
    {
      path: paths.rulesPage,
      name: 'rulesPage',
      component: () => import('@/components/UtilityPages/RulesPage/RulesPage.vue'),
      beforeEnter: isValidAuth,
      meta: { title: 'Rules - Iris' },
    },
    {
      path: paths.policiesPage,
      name: 'policiesPage',
      component: () => import('@/components/UtilityPages/PoliciesPage/PoliciesPage.vue'),
      beforeEnter: isValidAuth,
      meta: { title: 'Policies - Iris' },
    },
    {
      path: paths.userFunctionsPage,
      name: 'userFunctionsPage',
      component: () => import('@/components/UtilityPages/UserFunctionsPage/UserFunctionsPage.vue'),
      beforeEnter: isValidAuth,
      meta: { title: 'User Functions - Iris' },
    },
    {
      path: paths.search,
      name: 'search',
      component: () => import('@/components/UtilityPages/SearchPage/SearchPage.vue'),
      beforeEnter: isValidAuth,
      meta: { title: 'Search - Iris' },
    },
    // Make sure that this path is always the last one in the array. Since it matches any string, paths added after this one will be ignored.
    {
      path: paths.notFound,
      name: 'notFound',
      component: () => import('@/components/PageNotFound/PageNotFound.vue'),
      beforeEnter: isValidAuth,
      meta: { title: '404 - Page Not Found' },
    },
  ]

  router = new VueRouter({
    mode: 'history',
    routes: MAINTENANCE_MODE
      ? [
          // Disable navigation and show only the maintenance view
          // if application is in maintenance mode
          {
            path: '/*',
            name: 'maintenance',
            component: () => import('@/components/HomePage/Maintenance.vue'),
          },
        ]
      : irisRoutes,
  })

  router.afterEach(to => {
    if (to.meta?.title) {
      document.title = to.meta.title
    }
  })

  router.onError(error => {
    if (error.name === 'ChunkLoadError') {
      console.error(error)
      // Try and reload the page if a Webpack chunk fails to load
      // This usually happens when the content gets updated after a new release
      // or the user gets disconnected from the company VPN (and receives a 403 response from the server)
      window.location.reload()
    }
  })

  return router
}
